import { TOPBAR_HEIGHT } from 'views/global/TopBar/TopBar.styles';
import React, { useState, useEffect, useRef, Children } from 'react';
import { ReactNode } from 'react';
import { twc } from 'react-twc';

export const FlapMenuItem = twc.div`flex p-2 items-center justify-start w-full transition-all duration-200 ease-in-out hover:bg-[rgba(255,255,255,0.1)] 
 last:border-b-[1px] last:border-greyscale-veryDark first:border-t-[1px] first:border-greyscale-veryDark`;

const Triangle = twc.div`before:content-[''] before:absolute before:size-[15px] before:border-[none] before:border-t-[1px] before:border-l-[1px]
before:right-[10%] before:small:right-[3.5%] before:bg-greyscale-veryDark before:rotate-45 before:[clip-path:polygon(0%_0%,_100%_0%,_0%_100%)]
 data-[above=true]:before:-bottom-2 data-[above=false]:before:-top-2  data-[above=true]:before:rotate-[225deg]`;

const FlapMenu = twc(Triangle)`absolute -right-[0.4rem] small:-right-[1px]
  min-w-[50vw] small:min-w-[230px] max-w-[90vw]
 bg-[#24252b] transition-all ease-out duration-100 opacity-1 visible z-[2]
  shadow-[rgba(0,0,0,0.8)_0px_0px_12px_0px] rounded-lg border-[1px] border-solid border-greyscale-veryDark origin-top-right
  data-[above=true]:bottom-[34px] data-[above=false]:top-[calc(100%+8px)]`;

type FlapMenuButtonProps = {
  buttonIcon: JSX.Element;
  children: ReactNode;
  disabled?: boolean;
  childrenHeight?: number;
  closeOnClick?: boolean;
  preferUpwards?: boolean;
};

export const FlapMenuButton = ({
  buttonIcon: Button,
  children,
  disabled,
  childrenHeight = 50,
  closeOnClick = true,
  preferUpwards = false,
}: FlapMenuButtonProps) => {
  const node = useRef<HTMLDivElement>(null);
  const buttonNode = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const renderAbove = useRef(false);
  const buttonRef = useRef(null);

  const toggle = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();

    const topBarHeight = TOPBAR_HEIGHT;
    const buttonY = buttonNode.current?.getBoundingClientRect?.()?.top;
    const spaceAboveButton = buttonY ? buttonY - topBarHeight : 0;
    const spaceBelowButton = window.innerHeight - (spaceAboveButton ?? 0) + (buttonNode?.current?.offsetHeight ?? 0);
    const childrenCombinedHeight = childrenHeight * Children.toArray(children).length;

    const childrenFitAbove = spaceAboveButton > childrenCombinedHeight;
    const childrenFitBelow = spaceBelowButton > childrenCombinedHeight;

    if (!childrenFitBelow) {
      renderAbove.current = true;
    }

    if (childrenFitAbove === childrenFitBelow) {
      renderAbove.current = preferUpwards;
    }

    if (disabled) {
      return;
    }

    if (!isOpen) {
      setIsOpen(true);
      return;
    }

    const containsTarget = node.current?.contains(e.target as Node);

    if (!containsTarget) {
      setIsOpen(false);
    }

    if (containsTarget && closeOnClick) {
      setIsOpen(!isOpen);
    }
  };

  const handleClick = (e: MouseEvent) => {
    if (!node.current?.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClick);

    return () => document.removeEventListener('click', handleClick);
  });

  return (
    <div
      className="inline-block relative cursor-pointer w-[inherit] h-[inherit] flap-menu"
      onClick={toggle}
      ref={buttonNode}
      data-testid="flap-menu">
      <div ref={buttonRef}>{Button}</div>
      {isOpen && (
        <FlapMenu data-above={renderAbove.current} ref={node}>
          {children}
        </FlapMenu>
      )}
    </div>
  );
};
